import { useEffect, useRef, useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import './AdminLayout.css';

/** Only one sidebar item active; avoids /students matching /students/unpaid. */
export function isSidebarNavActive(pathname, path) {
  if (path === '/') {
    return pathname === '/';
  }
  if (path === '/students') {
    return pathname === '/students' || pathname.startsWith('/students/add');
  }
  if (path === '/students/unpaid') {
    return pathname === '/students/unpaid';
  }
  return pathname === path || pathname.startsWith(`${path}/`);
}

const navItems = [
  { path: '/', label: 'Dashboard', icon: '📊' },
  { path: '/students/unpaid', label: 'Unpaid Students', icon: '💰' },
  { path: '/students', label: 'Students', icon: '👥' },
  { path: '/courses', label: 'Courses', icon: '📚' },
  { path: '/teachers', label: 'Teachers', icon: '👨‍🏫' },
  { path: '/batches', label: 'Batches', icon: '📦' },
];

function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  const userEmail = user?.email || 'Admin';
  const userInitial = userEmail.charAt(0).toUpperCase();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    setProfileOpen(false);
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="admin-layout">
      <aside className="sidebar">
        <div className="sidebar-header">
          <h1 className="logo">Edu Pravahaa</h1>
          <span className="logo-badge">Admin LMS</span>
        </div>
        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={() =>
                `nav-item ${
                  isSidebarNavActive(location.pathname, item.path) ? 'nav-item--active' : ''
                }`
              }
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="admin-profile">
            <div className="admin-avatar">A</div>
            <div className="admin-info">
              <span className="admin-name">{user?.email || 'Admin'}</span>
              <span className="admin-role">LMS Administrator</span>
            </div>
          </div>
        </div>
      </aside>

      <div className="layout-main">
        <header className="topbar">
          <div className="topbar-left">
            <h2 className="topbar-title">Admin Dashboard</h2>
          </div>
          <div className="topbar-right" ref={profileRef}>
            <button
              type="button"
              className="topbar-profile-btn"
              onClick={() => setProfileOpen((open) => !open)}
              aria-expanded={profileOpen}
              aria-haspopup="menu"
              aria-label="Open profile menu"
            >
              <span className="topbar-profile-avatar" aria-hidden>
                {userInitial}
              </span>
              <span className="topbar-profile-label">Profile</span>
              <span className="topbar-profile-chevron" aria-hidden>
                ▾
              </span>
            </button>
            {profileOpen ? (
              <div className="topbar-profile-menu" role="menu">
                <div className="topbar-profile-menu-header">
                  <span className="topbar-profile-menu-email">{userEmail}</span>
                  <span className="topbar-profile-menu-role">LMS Administrator</span>
                </div>
                <button
                  type="button"
                  className="topbar-profile-menu-item topbar-profile-menu-item--danger"
                  role="menuitem"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </div>
            ) : null}
          </div>
        </header>

        <main className="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default AdminLayout;
